import React, { useEffect, useState } from 'react';
import './BookingInput.scss';
import { useLocation, useNavigate } from 'react-router-dom';
import PlaceSearch from "../map/Googlemap";
import { Button, Modal } from "react-bootstrap";
import { AddOutlined, RemoveOutlined } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { calculateBooking, getSpecialDayChargePercentage, isSpecialDate } from '../../utils/help';
import { extraChildrenAddPrice } from '../../utils/Constants';

const phoneNumber = "+34617243659";
const MAX_HOURS = 10;
const MAX_CHILDREN = 5;
const MAX_SUMMARY_CHARS = 500;

const options = [
    "English", "Spanish", "French", "German", "Chinese", "Arabic",
    "Portuguese", "Russian", "Japanese", "Korean", "Italian", "Dutch",
    "Polish", "Swedish", "Turkish", "Indonesian", "Thai", "Vietnamese", "Hebrew"
];

const BookingInput = () => {
    const navigate = useNavigate();
    const { state } = useLocation();
    const { categoryId, categoryName, categoryImage, price } = state || {};

    const [editedStreet, setEditedStreet] = useState('');
    const [calculatedPrice, setCalculatedPrice] = useState(price);
    const [editedHouse, setEditedHouse] = useState('');
    const [editedApt, setEditedApt] = useState('');
    const [summary, setSummary] = useState('');
    const [editedHours, setEditedHours] = useState(2);
    const [showMap, setShowMap] = useState(false);
    const [latitude, setLatitude] = useState(null);
    const [longitude, setLongitude] = useState(null);

    const [error, setError] = useState(null);
    const [address, setAddress] = useState("");
    const [date, setDate] = useState("");
    const [time, setTime] = useState("");

    const [children, setChildren] = useState([]);
    const [newChild, setNewChild] = useState({ name: '', age: '', gender: 'Male', language: 'English' });
    const [editingIndex, setEditingIndex] = useState(null);

    const [showHoursNotification, setShowHoursNotification] = useState(false);
    const [showChildrenNotification, setShowChildrenNotification] = useState(false);

    useEffect(() => {
        if (!navigator.geolocation) return setError("Geolocation not supported");
        navigator.geolocation.getCurrentPosition(
            ({ coords }) => {
                if (!longitude || !latitude) {
                    setLatitude(coords.latitude);
                    setLongitude(coords.longitude);
                    setError(null);
                }
            },
            (err) => {
                setError(err.message);
                setLatitude(null);
                setLongitude(null);
            }
        );
    }, [showMap]);

    useEffect(() => {
        let base = price;
        if (children.length > 2) {
            base = base + ((children.length - 2) * extraChildrenAddPrice);
        }
        let specialDateChargePercentage = 1;
        if (date) {
            specialDateChargePercentage = getSpecialDayChargePercentage(date);
            base = isSpecialDate(date, base);
        }
        let taxiFee = 0;
        if (time) {
            const { taxiFee: fee } = calculateBooking(time, editedHours);
            taxiFee = fee * specialDateChargePercentage;
        }
        base = (base * editedHours) + taxiFee || 0;
        setCalculatedPrice(base);
    }, [time, editedHours, children, price, date]);

    const handleTimeChange = (time) => {
        const dateObj = new Date(time);
        let hours = dateObj.getHours();
        const minutes = String(dateObj.getMinutes()).padStart(2, "0");
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12 || 12;
        setTime(`${hours}:${minutes} ${ampm}`);
    };

    const handleDateChange = (newDate) => setDate(newDate);

    const handleIncrementHours = () => {
        if (editedHours >= MAX_HOURS) {
            setShowHoursNotification(true);
        } else {
            setEditedHours(prev => prev + 1);
        }
    };

    const handleDecrementHours = () => {
        setEditedHours(prev => Math.max(2, prev - 1));
        setShowHoursNotification(false);
    };

    const handleSummaryChange = (e) => {
        if (e.target.value.length <= MAX_SUMMARY_CHARS) setSummary(e.target.value);
    };

    const handleAddOrUpdateChild = () => {
        const { name, age, gender, language } = newChild;
        if (!name.trim() || !age.trim() || !gender.trim() || !language.trim()) {
            return alert("Please fill in all child details.");
        }
        if (editingIndex !== null) {
            const updated = [...children];
            updated[editingIndex] = newChild;
            setChildren(updated);
            setEditingIndex(null);
        } else {
            if (children.length >= MAX_CHILDREN) {
                setShowChildrenNotification(true);
                return;
            }
            setChildren([...children, newChild]);
            if (children.length + 1 < MAX_CHILDREN) setShowChildrenNotification(false);
        }
        setNewChild({ name: '', age: '', gender: 'Male', language: 'English' });
    };

    const handleEditChild = (index) => {
        setNewChild(children[index]);
        setEditingIndex(index);
    };

    const handleRemoveChild = (index) => {
        const updated = [...children];
        updated.splice(index, 1);
        setChildren(updated);
        setShowChildrenNotification(false);
        if (editingIndex === index) {
            setNewChild({ name: '', age: '', gender: 'Male', language: 'English' });
            setEditingIndex(null);
        }
    };

    const validateFields = () => {
        if (!address.trim() || !date.trim() || !time.trim()) return false;
        if (categoryName !== 'Pet sitting services' && categoryName !== 'Cleaning Services') {
            if (!children?.length) return false;
            return children.every(child => child.name && child.age && child.gender);
        }
        return true;
    };

    const handleContinue = () => {
        if (!validateFields()) return alert("Please fill address, children, date and time its mandatory");
        navigate('/nanyList', {
            state: {
                address, date, time,
                counter: editedHours,
                divs: children,
                latitude, longitude, categoryId,
                appartment: editedApt,
                house: editedHouse,
                street: editedStreet,
                summary,
            },
        });
    };

    return (
        <div className="nannycategories">
            <div className="banner_categories nannycategories_bg">
                <div className="heading_categories container">
                    <h1><span>Add Booking Details</span></h1>
                </div>
            </div>

            <div className="preview-container">
                <div className="service-card">
                    {categoryImage && <img src={categoryImage} alt={categoryName} />}
                    <div className="service-details">
                        {categoryName && <h3>{categoryName}</h3>}
                        <p>Service Category</p>
                    </div>
                    <div className="service-price">
                        <p>€{Number(calculatedPrice * 1.21).toFixed(2)}</p>
                    </div>
                </div>

                {/* ── Location ── */}
                <div className="preview-section">
                    <h3>Location</h3>
                    <div className="field-row">
                        <strong>Address:</strong>
                        <input type="text" value={address} readOnly onClick={() => setShowMap(true)} className="field-input field-input--clickable" />
                    </div>
                    <div className="field-row">
                        <strong>Street Name:</strong>
                        <input className="field-input" value={editedStreet} onChange={e => setEditedStreet(e.target.value)} />
                    </div>
                    <div className="field-row">
                        <strong>House Number:</strong>
                        <input className="field-input" value={editedHouse} onChange={e => setEditedHouse(e.target.value)} />
                    </div>
                    <div className="field-row">
                        <strong>Apartment Number:</strong>
                        <input className="field-input" value={editedApt} onChange={e => setEditedApt(e.target.value)} />
                    </div>
                </div>

                {/* ── Date & Time ── */}
                <div className="preview-section">
                    <h3>Date & Time</h3>

                    <div className="field-row">
                        <strong>Date:</strong>
                        <input className="field-input" type="date" value={date} onChange={(e) => handleDateChange(e.target.value)} />
                    </div>

                    <div className="field-row">
                        <strong>Time:</strong>
                        <div className="field-control">
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <TimePicker onChange={handleTimeChange} />
                            </LocalizationProvider>
                        </div>
                    </div>

                    {/* Hours row: label + stepper inline, notification below full-width */}
                    <div className="field-row field-row--col">
                        <div className="field-row__inline">
                            <strong>Number of Hours:</strong>
                            <div className="hours-control">
                                <input type="number" value={editedHours} readOnly />
                                <AddOutlined onClick={handleIncrementHours} className="hours-icon" />
                                <RemoveOutlined onClick={handleDecrementHours} className="hours-icon" />
                            </div>
                        </div>
                        {showHoursNotification && (
                            <div className="booking-notification booking-notification--info">
                                <p className="notif-title">⏱️ Need more than {MAX_HOURS} hours?</p>
                                <p>Please contact us to arrange a custom booking:</p>
                                <p>
                                    📧 <a href="mailto:info@nanny-line.com">info@nanny-line.com</a>
                                    <span className="notif-sep"> | </span>
                                    💬 <a href={`https://wa.me/${phoneNumber}`} target="_blank" rel="noreferrer">WhatsApp</a>
                                </p>
                            </div>
                        )}
                    </div>

                    {/* Summary row */}
                    <div className="field-row field-row--col">
                        <strong>Summary:</strong>
                        <div className="field-control">
                            <textarea
                                value={summary}
                                onChange={handleSummaryChange}
                                rows={4}
                                placeholder={`Max ${MAX_SUMMARY_CHARS} characters`}
                            />
                            <span className={`char-count${summary.length >= MAX_SUMMARY_CHARS ? ' char-count--limit' : ''}`}>
                                {summary.length} / {MAX_SUMMARY_CHARS}
                            </span>
                        </div>
                    </div>
                </div>

                {/* ── Children Details ── */}
                {categoryName !== 'Pet sitting services' && categoryName !== 'Cleaning Services' && (
                    <div className="preview-section">
                        <h3>Children Details</h3>

                        {children.length > 0 && (
                            <div className="table-scroll-wrapper">
                                <table className="children-table">
                                    <thead>
                                        <tr><th>No</th><th>Name</th><th>Age</th><th>Gender</th><th>Language</th><th>Actions</th></tr>
                                    </thead>
                                    <tbody>
                                        {children.map((child, idx) => (
                                            <tr key={idx}>
                                                <td>{idx + 1}</td>
                                                <td className="name-cell">{child.name}</td>
                                                <td>{child.age}</td>
                                                <td>{child.gender}</td>
                                                <td>{child.language}</td>
                                                <td className="action-btns">
                                                    <button onClick={() => handleEditChild(idx)}>Edit</button>
                                                    <button onClick={() => handleRemoveChild(idx)}>Remove</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {showChildrenNotification && (
                            <div className="booking-notification booking-notification--warning">
                                <p className="notif-title">👧 Maximum of {MAX_CHILDREN} children reached.</p>
                                <p>For 6 or more children, please <strong>book a second nanny</strong> to ensure proper care for all children.</p>
                            </div>
                        )}

                        {(children.length < MAX_CHILDREN || editingIndex !== null) && (
                            <div className="add-child-form">
                                <input type="text" placeholder="Name" value={newChild.name} onChange={e => setNewChild({ ...newChild, name: e.target.value })} />
                                <input type="text" placeholder="Age" value={newChild.age} onChange={e => setNewChild({ ...newChild, age: e.target.value })} />
                                <select value={newChild.gender} onChange={e => setNewChild({ ...newChild, gender: e.target.value })}>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                    <option value="Other">Other</option>
                                </select>
                                <select value={newChild.language} onChange={e => setNewChild({ ...newChild, language: e.target.value })}>
                                    {options.map((elm, idx) => (
                                        <option key={idx} value={elm}>{elm}</option>
                                    ))}
                                </select>
                                <button type="button" onClick={handleAddOrUpdateChild}>
                                    {editingIndex !== null ? "Update" : "Add"}
                                </button>
                            </div>
                        )}
                    </div>
                )}

                <div className="summary-btns">
                    <button className="cancel-btn" onClick={() => navigate('/nannycategories')}>Cancel</button>
                    <button className="continue-btn" onClick={handleContinue}>Continue</button>
                </div>
            </div>

            <Modal show={showMap} onHide={() => setShowMap(false)} className="add_detalis">
                <Modal.Header closeButton><Modal.Title>My Address</Modal.Title></Modal.Header>
                <div style={{ paddingBottom: 20 }}>
                    {error && <p>Error: {error}</p>}
                    {latitude && longitude && (
                        <PlaceSearch
                            lat={latitude}
                            lng={longitude}
                            setAddress={setAddress}
                            setLatitude={setLatitude}
                            setLongitude={setLongitude}
                            setEditedApt={setEditedApt}
                            setEditedHouse={setEditedHouse}
                            setEditedStreet={setEditedStreet}
                        />
                    )}
                    <div style={{ textAlign: "center", zIndex: 9999 }}>
                        <Button onClick={() => setShowMap(false)}>OK</Button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default BookingInput;
